import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import LookupComponent from "@/common/components/lookupComponent";
import { LookupEntityType, UpdatedUserRole } from "@/common/enums/enums";
import { withContext } from "@/common/utils/withContext";
import { Close } from "@mui/icons-material";
import {
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { requiredFields } from "@/modules/organization/shared/schemas/siteAreaFormSchema";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const AddConnectedAccount = (props: any) => {
  const { t: translate } = useTranslation();
  const connectedAccountForm: any = props.connectedAccountForm;
  const UserLookupRef: any = useRef(null);
  const [state, setStateData] = useState({
    showUserLookUpModal: false,
    userName: "",
    // Added for Site Lookup
    showSiteLookUpModal: false,
  } as any);
  const setUserFilter = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        showUserLookUpModal: true,
      };
    });
  };

  const onUserSelected = (users: any) => {
    connectedAccountForm.setValue("userID", users[0].id, {
      shouldDirty: true,
      shouldValidate: true,
    });

    connectedAccountForm.setValue(
      "user",
      {
        name: users[0].name,
        email: users[0].email,
        firstName: users[0].firstName,
      },
      { shouldValidate: true, shouldDirty: true }
    );
    setStateData((currentData: any) => {
      return {
        ...currentData,
        userName: users[0].firstName + " " + users[0].name,
        user: {
          firstName: users[0].firstName,
          name: users[0].name,
          email: users[0].email,
        },
        showUserLookUpModal: false,
      };
    });
    connectedAccountForm.trigger("user");
  };

  const onUserFilterModalClose = () => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        showUserLookUpModal: false,
      };
    });
  };

  const onRemoveUser = (event: any) => {
    event.stopPropagation();
    connectedAccountForm.setValue(
      "user",
      {
        name: "",
        firstName: "",
        email: "",
      },
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    );
    connectedAccountForm.setValue("userID", "");
    connectedAccountForm.trigger("user");

    setStateData((currentData: any) => {
      return {
        ...currentData,
        userName: "",
      };
    });
  };

  const onClickSiteLookUpModal = () => {
    setStateData((currentData: any) => ({
      ...currentData,
      showSiteLookUpModal: true,
    }));
  };

  const onCloseSiteLookUpModal = () => {
    setStateData((currentData: any) => ({
      ...currentData,
      showSiteLookUpModal: false,
    }));
  };

  const onSiteSelect = (sites: any[]) => {
    connectedAccountForm.setValue("sites", sites || [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    connectedAccountForm.trigger("sites");

    setStateData((currentData: any) => ({
      ...currentData,
      showSiteLookUpModal: false,
    }));
  };
  useEffect(() => {
    connectedAccountForm.reset();
  }, [props?.selectedUserType]);

  return (
    <>
      {state.showUserLookUpModal && (
        <LookupComponent
          title={`${translate("users.select_users")}`}
          showLookupModal={state.showUserLookUpModal}
          onModalClose={onUserFilterModalClose}
          selectionMode="single"
          entityType={LookupEntityType.SESSION_USER}
          params={[
            { key: "Issuer", value: true },

            ...(props?.superAdmin
              ? [
                  { key: "role", value: UpdatedUserRole.ADMIN },
                  { key: "tenantId", value: props?.tenantId },
                ]
              : [{ key: "Role", value: UpdatedUserRole.SITE_OWNER }]),
          ]}
          onSelect={onUserSelected}
          defaultSortColumn="name"
          isConnected={true}
          data-cy="session-select-user-lookup"
        />
      )}

      {state.showSiteLookUpModal && (
        <LookupComponent
          title={`${translate("site_areas.select_site")}`}
          showLookupModal={state.showSiteLookUpModal}
          onModalClose={onCloseSiteLookUpModal}
          selectionMode="multiple"
          defaultSortColumn="name"
          entityType={LookupEntityType.SITE}
          onSelect={onSiteSelect}
          params={[{ key: "Issuer", value: true }]}
        />
      )}

      <Grid
        display="flex"
        flexDirection="column"
        width={"100%"}
        borderRadius={"8px"}
        border={"1px solid #E2E2E2"}
        padding={"16px"}
        paddingBottom={"20px"}
        marginBottom={2}
      >
        <Grid
          width={"100%"}
          height={"20px"}
          padding={"0 0 4px 0"}
          sx={{
            borderBottomWidth: "1px",
          }}
        >
          <Grid
            sx={{
              fontFamily: "Roboto",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16.41px",
              textAlign: "left",
              marginBottom: "16px",
            }}
          >
            User Details
          </Grid>
          <Grid display={"flex"} gap={"16px"} alignItems={"center"}>
            <FormControlLabel
              value="existing"
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                margin: 0,
                padding: 0,
              }}
              control={
                <Radio
                  value="existing"
                  checked={props?.selectedUserType === "existing"}
                  onChange={() => props?.setSelectedUserType("existing")}
                  sx={{
                    color: "#3D3AD3",
                    padding: "0 !important",
                    "& .MuiSvgIcon-root": {
                      width: "13px",
                      height: "13px",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    color: "#242424",
                    lineHeight: "0px",
                  }}
                  data-cy={"radio-close"}
                >
                  Existing User
                </Typography>
              }
            />
            <FormControlLabel
              value="new"
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                margin: 0,
                padding: 0,
              }}
              control={
                <Radio
                  value="new"
                  checked={props?.selectedUserType === "new"}
                  onChange={() => props?.setSelectedUserType("new")}
                  sx={{
                    color: "#3D3AD3",
                    padding: "0 !important",
                    "& .MuiSvgIcon-root": {
                      width: "13px",
                      height: "13px",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    color: "#242424",
                    lineHeight: "0px",
                  }}
                  data-cy={"radio-open"}
                >
                  New User
                </Typography>
              }
            />
          </Grid>
        </Grid>
        {props?.selectedUserType === "existing" && (
          <Grid
            container
            rowSpacing={1}
            columnSpacing={2}
            sx={{ marginTop: "36px" }}
          >
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="companyName"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="company"
                    label={`${translate("companies.company_name")}`}
                    fullWidth
                    disabled={props.isDisabled}
                    variant="standard"
                    error={!!connectedAccountForm.formState?.errors.companyName}
                    helperText={connectedAccountForm.formState?.errors.companyName?.message?.toString()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="user"
                control={connectedAccountForm.control}
                render={({ field }) => {
                  const userErrors =
                    connectedAccountForm.formState?.errors?.user || {};
                  const hasError =
                    !!userErrors?.name ||
                    !!userErrors?.firstName ||
                    !!userErrors?.email;

                  const getHelperText = () => {
                    if (hasError) return "User is required";
                    return "";
                  };

                  return (
                    <EvesTextbox
                      {...field}
                      value={
                        field.value?.name && field.value?.firstName
                          ? `${field.value?.name} ${field.value?.firstName}`
                          : ""
                      }
                      id="User-standard-search"
                      label={`${translate("general.search_user")}`}
                      data-cy="User-filter"
                      type="text"
                      variant="standard"
                      error={hasError}
                      helperText={getHelperText()}
                      onClick={setUserFilter}
                      ref={UserLookupRef}
                      InputProps={{
                        endAdornment: field.value?.name && (
                          <InputAdornment
                            position="end"
                            style={{ cursor: "pointer" }}
                          >
                            {" "}
                            <Close
                              sx={{ color: "black", height: "15px" }}
                              id="user"
                              //onClick={onRemoveUser}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveUser(e);
                                field.onChange({
                                  name: "",
                                  email: "",
                                  firstName: "",
                                });
                              }}
                            />{" "}
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <Controller
                name="sites"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="site"
                    label={`${translate("sites.site_required")}`}
                    fullWidth
                    onClick={onClickSiteLookUpModal}
                    disabled={props.isDisabled}
                    required={requiredFields["sites"]}
                    variant="standard"
                    value={
                      field.value?.length > 0
                        ? `${field.value[0].name}${
                            field.value.length > 1
                              ? ` (+${field.value.length - 1})`
                              : ""
                          }`
                        : ""
                    }
                    InputProps={{
                      endAdornment: field.value?.length ? (
                        <InputAdornment
                          position="end"
                          style={{ cursor: "pointer" }}
                        >
                          <Close
                            sx={{ color: "black", height: "15px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              field.onChange([]);
                            }}
                          />
                        </InputAdornment>
                      ) : (
                        <InputAdornment
                          position="end"
                          style={{ cursor: "pointer" }}
                        >
                          <IconButton disabled={props.isDisabled}>
                            <ArrowDropDownIcon
                              sx={{
                                color: "black",
                                height: "20px",
                                width: "20px",
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                      readOnly: true,
                    }}
                    error={!!connectedAccountForm.formState?.errors.sites}
                    helperText={`${translate(
                      connectedAccountForm.formState?.errors.sites?.message ||
                        " "
                    )}`}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        {props?.selectedUserType === "new" && (
          <Grid
            container
            direction="row"
            rowSpacing={1}
            columnSpacing={2}
            sx={{
              marginTop: "36px",
            }}
          >
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="companyName"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="companyName"
                    label={`${translate("companies.company_name")}`}
                    fullWidth
                    disabled={props.isDisabled}
                    variant="standard"
                    error={
                      !!connectedAccountForm.formState?.errors?.companyName
                    }
                    helperText={connectedAccountForm.formState?.errors.companyName?.message?.toString()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="user.email"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="user.email"
                    label={`${translate("companies.email")}`}
                    fullWidth
                    disabled={props.isDisabled}
                    variant="standard"
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    error={
                      !!connectedAccountForm.formState?.errors?.user?.email
                    }
                    helperText={connectedAccountForm.formState?.errors?.user?.email?.message?.toString()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="user.firstName"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="user.firstName"
                    label={`${translate("companies.first_name")}`}
                    fullWidth
                    disabled={props.isDisabled}
                    variant="standard"
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    error={
                      !!connectedAccountForm.formState?.errors?.user?.firstName
                    }
                    helperText={connectedAccountForm.formState?.errors?.user?.firstName?.message?.toString()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mb={1}>
              <Controller
                name="user.name"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="user.name"
                    label={`${translate("companies.last_name")}`}
                    fullWidth
                    disabled={props.isDisabled}
                    variant="standard"
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    error={!!connectedAccountForm.formState?.errors?.user?.name}
                    helperText={connectedAccountForm.formState?.errors?.user?.name?.message?.toString()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <Controller
                name="sites"
                control={connectedAccountForm.control}
                render={({ field }) => (
                  <EvesTextbox
                    {...field}
                    id="sites"
                    label={`${translate("sites.site_required")}`}
                    fullWidth
                    onClick={onClickSiteLookUpModal}
                    disabled={props.isDisabled}
                    required={requiredFields["sites"]}
                    variant="standard"
                    value={
                      field.value?.length > 0
                        ? `${field.value[0].name}${
                            field.value.length > 1
                              ? ` (+${field.value.length - 1})`
                              : ""
                          }`
                        : ""
                    }
                    InputProps={{
                      endAdornment: field.value?.length ? (
                        <InputAdornment
                          position="end"
                          style={{ cursor: "pointer" }}
                        >
                          <Close
                            sx={{ color: "black", height: "15px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              field.onChange([]);
                            }}
                          />
                        </InputAdornment>
                      ) : (
                        <InputAdornment
                          position="end"
                          style={{ cursor: "pointer" }}
                        >
                          <IconButton disabled={props.isDisabled}>
                            <ArrowDropDownIcon
                              sx={{
                                color: "black",
                                height: "20px",
                                width: "20px",
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                      readOnly: true,
                    }}
                    error={!!connectedAccountForm.formState?.errors.sites}
                    helperText={`${translate(
                      connectedAccountForm.formState?.errors.sites?.message ||
                        " "
                    )}`}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default withContext(AddConnectedAccount);
